// ============================================================================
// Research Helper Functions
// ============================================================================
// Utility functions for the research section:
// - Ghost CMS API fetching (light and full data)
// - Article categorization and filtering
// - TF-IDF article recommendations (runtime fallback)
// - URL transformations and formatting
//
// Key functions:
// - fetchArticlesLight(): Fast initial load (92% smaller, no HTML content)
// - fetchAllArticles(): Full article data with HTML
// - categorizeArticles(): Group articles by tag for filtering
// - getRecommendedByKeywords(): Runtime TF-IDF recommendations
//
// Note: This file mirrors build.py's recommendation algorithm for client-side fallback.
// ============================================================================

import { GhostArticle, GhostTag, CategorizedArticles } from '../types/research';

// ============================================================================
// Utility Functions - Sorting & Comparison
// ============================================================================

// Sort articles by publish date (newest first)
// Using Date.parse(); cleaner than new Date().getTime()
export const sortByNewest = (a: GhostArticle, b: GhostArticle): number => {
    return Date.parse(b.published_at) - Date.parse(a.published_at);
};

// Sort articles by publish date (oldest first)
export const sortByOldest = (a: GhostArticle, b: GhostArticle): number => {
    return Date.parse(a.published_at) - Date.parse(b.published_at);
};

// ============================================================================
// API & Data Fetching
// ============================================================================

// Get Research API URL from data attribute (set by HTML template based on environment)
export const getResearchApiUrl = (): string => {
    const container = document.getElementById('research-content');
    return container?.dataset.apiUrl || 'https://dev.othram.com/api/research-cms';
};

// Fetch lightweight articles for homepage display (client-side filtered)
export const fetchArticlesLight = async (): Promise<GhostArticle[]> => {
    try {
        const response = await fetch(`${getResearchApiUrl()}/posts/?include=tags&limit=100&page=1`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();

        const articles = data.posts || [];

        // Client-side filtering to keep only essential fields for homepage
        // This reduces memory usage and prevents heavy HTML content from being processed
        return articles.map((article: any) => ({
            id: article.id,
            title: article.title,
            slug: article.slug,
            feature_image: article.feature_image,
            published_at: article.published_at,

            tags: article.tags,
            excerpt: article.excerpt,
            custom_excerpt: article.custom_excerpt
            // Explicitly exclude: html (heavy content)
        }));
    } catch (error) {
        console.error('Error fetching lightweight articles:', error);
        throw error;
    }
};

// Fetch all articles from Othram Research CMS (with full content including HTML)
// Prefers preloaded articles from articles.js (built at deploy time with local image paths)
// Falls back to Ghost API if preloaded data unavailable (dev mode)
export const fetchAllArticles = async (): Promise<GhostArticle[]> => {
    try {
        // Check if preloaded articles are available (built at deploy time)
        if (typeof window !== 'undefined' && (window as any).GHOST_ARTICLES) {
            console.log('Using preloaded articles with local image paths');
            const data = (window as any).GHOST_ARTICLES;
            const articles = data.posts || [];

            // Replace research.othram.com URLs with current domain
            const currentOrigin = window.location.origin;
            return articles.map((article: any) => ({
                ...article,
                url: article.url ? article.url.replace('https://research.othram.com', `${currentOrigin}/research`) : article.url
            }));
        }

        // Fallback: Fetch from Ghost API (slower, uses Ghost CDN images)
        console.log('Preloaded articles not found, fetching from Ghost API...');
        const response = await fetch(`${getResearchApiUrl()}/posts/?include=tags&limit=100&page=1`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();

        const articles = data.posts || [];

        // Replace research.othram.com URLs with current domain
        const currentOrigin = window.location.origin;
        return articles.map((article: any) => ({
            ...article,
            url: article.url ? article.url.replace('https://research.othram.com', `${currentOrigin}/research`) : article.url
        }));
    } catch (error) {
        console.error('Error fetching articles:', error);
        throw error;
    }
};

// Categorize articles by tags
export const categorizeArticles = (articles: GhostArticle[]): CategorizedArticles => {
    const categorized: CategorizedArticles = {};

    articles.forEach((article: GhostArticle) => {
        if (article.tags && article.tags.length > 0) {
            const tag: GhostTag = article.tags[0];
            if (!categorized[tag.slug]) {
                categorized[tag.slug] = {
                    name: tag.name,
                    slug: tag.slug,
                    articles: []
                };
            }
            categorized[tag.slug].articles.push(article);
        }
    });

    return categorized;
};

// Convert Ghost article to exact format Helix expects with click handling
export const convertArticleForHelix = (article: GhostArticle, handleArticleNavigation: (slug: string) => void) => {
    if (!article) {
        console.warn('convertArticleForHelix: article is undefined');
        return {
            imageUrl: '/img/icons/microscope.svg',
            imageAlt: 'Default article',
            tag: 'Article',
            title: 'Loading...',
            slug: '',
            url: '#',
            onClick: () => { }
        };
    }

    return {
        imageUrl: article.feature_image || '/img/icons/microscope.svg',
        imageAlt: article.title || 'Article',
        tag: article.tags?.[0] ? formatTagName(article.tags[0].name) : 'Article',
        title: article.title || 'Untitled',
        slug: article.slug || '',
        // Use current origin to match integrated research section (not old subdomain)
        url: `${window.location.origin}/research/${article.tags?.[0]?.slug || 'editorial'}/${article.slug}`,
        onClick: () => handleArticleNavigation(article.slug)
    };
};

// Format tag names for display
export const formatTagName = (tagName: string): string => {
    const tagMap: { [key: string]: string } = {
        'peer-reviewed': 'Peer-Reviewed Article',
        'peer-review': 'Peer-Reviewed Article',
        'insights': 'Insights',
        'webinar': 'Webinar',
        'product-preview': 'Product Preview'
    };

    const lowerTag = tagName.toLowerCase();
    return tagMap[lowerTag] || tagName;
};

// Get category description for display
export const getCategoryDescription = (categorySlug: string): string => {
    const descriptions: { [key: string]: string } = {
        'peer-reviewed': 'Peer-reviewed scientific publications advancing forensic genomics research.',
        'explainer-article': 'In-depth explanations of complex forensic science concepts and methodologies.',
        'feature-preview': 'Previews of upcoming Othram technologies and research initiatives.',
        'editorial': 'Editorial perspectives on the future of forensic science and genetic genealogy.',
        'webinar': 'Educational webinars and presentations from Othram experts.',
        'insights': 'Data-driven insights and analysis from real forensic cases.',
        'explainer': 'In-depth explanations of complex forensic science concepts and methodologies.'
    };

    return descriptions[categorySlug] || 'Research articles and insights advancing forensic science.';
};

// Stop words to filter out when extracting keywords
// Synced with build.py STOP_WORDS (387 words - 5 rounds of curation)
// IMPORTANT: Keep this synchronized with build.py for consistent recommendations
const STOP_WORDS = new Set([
    'the', 'a', 'an', 'and', 'or', 'but', 'nor', 'yet', 'so', 'in',
    'on', 'at', 'to', 'for', 'of', 'with', 'from', 'by', 'about', 'as',
    'into', 'through', 'after', 'before', 'between', 'under', 'over', 'above', 'below', 'during',
    'within', 'without', 'upon', 'is', 'are', 'was', 'were', 'be', 'been', 'being',
    'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'should', 'could',
    'can', 'may', 'might', 'i', 'you', 'he', 'she', 'it', 'we', 'they',
    'me', 'him', 'her', 'us', 'them', 'my', 'your', 'his', 'its', 'our',
    'their', 'this', 'that', 'these', 'those', 'what', 'which', 'who', 'when', 'where',
    'why', 'how', 'all', 'each', 'every', 'both', 'few', 'more', 'most', 'other',
    'some', 'such', 'no', 'not', 'only', 'own', 'same', 'than', 'too', 'very',
    'one', 'two', 'three', 'based', 'high', 'low', 'best', 'better', 'well', 'also',
    'however', 'therefore', 'thus', 'hence', 'involved', 'promising', 'leaves', 'significant', 'important', 'critical',
    'essential', 'major', 'minor', 'general', 'specific', 'particular', 'certain', 'various', 'several', 'many',
    'much', 'little', 'large', 'small', 'new', 'old', 'first', 'last', 'next', 'previous',
    'current', 'recent', 'future', 'past', 'present', 'othram', 'othramsm', 'othrams', 'forensic', 'left',
    'don', 'didn', 'won', 'wouldn', 'shouldn', 'couldn', 'isn', 'wasn', 'weren', 'haven',
    'hasn', 'hadn', 'across', 'any', 'approach', 'because', 'bring', 'come', 'developed', 'enable',
    'especially', 'even', 'example', 'get', 'help', 'here', 'information', 'just', 'let', 'like',
    'make', 'making', 'need', 'often', 'operates', 'out', 'part', 'possible', 'potential', 'single',
    'started', 'team', 'time', 'together', 'use', 'used', 'using', 'way', 'while', 'work',
    'working', 'allowing', 'available', 'beyond', 'built', 'common', 'complex', 'comprehensive', 'different', 'further',
    'individual', 'now', 'particularly', 'person', 'results', 'short', 'there', 'unique', 'whether', 'years',
    'accelerates', 'access', 'applies', 'central', 'compromise', 'conclusion', 'defensible', 'dense', 'difference', 'edge',
    'end', 'enter', 'experts', 'files', 'format', 'formats', 'free', 'funded', 'highest', 'imagine',
    'implementing', 'inadequate', 'index', 'invite', 'know', 'limited', 'list', 'long', 'metric', 'necessary',
    'non', 'nuanced', 'open', 'organizations', 'pairwise', 'picture', 'population', 'quality', 'real', 'represented',
    'review', 'right', 'scale', 'securing', 'severely', 'significantly', 'structures', 'sufficient', 'transformative', 'updates',
    'upload', 'went', 'year', 'alone', 'automatically', 'became', 'challenging', 'contextual', 'deeper', 'defined',
    'deserve', 'despite', 'detailed', 'dramatically', 'driven', 'else', 'enabling', 'enhancing', 'entered', 'ever',
    'existing', 'expanding', 'expertise', 'five', 'full', 'greater', 'guiding', 'higher', 'immediately', 'importance',
    'improved', 'include', 'incorrect', 'increasingly', 'infer', 'known', 'larger', 'laying', 'leverage', 'leveraging',
    'longer', 'measure', 'measured', 'name', 'nearly', 'obtained', 'offer', 'operate', 'organizing', 'outside',
    'overlapping', 'perform', 'physical', 'piece', 'pieces', 'placed', 'practice', 'produce', 'programs', 'realize',
    'relatively', 'representing', 'represents', 'reveals', 'rigor', 'science', 'scientific', 'service', 'similarly', 'smaller',
    'someone', 'soon', 'suggest', 'supports', 'third', 'transformed', 'until', 'users', 'whose', 'believe',
    'ways', 'doesn', 'moreover', 'addressed', 'seen', 'almost', 'recently', 'show', 'try', 'day',
    'air', 'brings', 'back', 'strong', 'helped', 'amount', 'fully', 'shorter', 'needs', 'lots',
    'whole', 'read', 'began', 'generally', 'although', 'sufficiently', 'indicates', 'aren', 'call', 'potentially',
    'effort', 'easily', 'requiring', 'join', 'faced', 'performed', 'wide',
]);

// Extract meaningful keywords from text (title, excerpt, etc.)
export const extractKeywords = (text: string): string[] => {
    if (!text) return [];

    const words = text
        .toLowerCase()                                      // Convert to lowercase
        .replace(/[^\w\s'-]/g, ' ')                         // Remove punctuation except hyphens/apostrophes
        .split(/\s+/)                                       // Split into words
        .map(word => word.replace(/^[''-]+|[''-]+$/g, ''))  // Remove leading/trailing punctuation
        .filter(word => word.length > 2)                    // Remove very short words
        .filter(word => !STOP_WORDS.has(word));            // Remove stop words

    // Remove duplicates by converting to Set and back to array
    return Array.from(new Set(words));
};

// Type for scored article results
type ScoredArticle = { article: GhostArticle; score: number };

// Get recommended articles based on title + excerpt keyword matching
export const getRecommendedByKeywords = (
    currentArticle: GhostArticle,
    allArticles: GhostArticle[],
    count: number = 3
): GhostArticle[] => {
    // Build searchable text from title and excerpts
    const getSearchableText = (article: GhostArticle): string => {
        let text = article.title || '';

        // Add custom excerpt if available (preferred, written by authors)
        if (article.custom_excerpt) {
            text += ' ' + article.custom_excerpt;
        }
        // Otherwise use auto-generated excerpt
        else if (article.excerpt) {
            text += ' ' + article.excerpt;
        }

        return text;
    };

    // Extract keywords from current article
    const currentText = getSearchableText(currentArticle);
    const currentKeywords = extractKeywords(currentText);

    // Use Set for O(1) keyword lookup instead of O(n) Array.includes()
    const currentKeywordsSet = new Set(currentKeywords);

    // Silent fallback computation - no logging for production

    // Filter out current article
    const otherArticles = allArticles.filter((a: GhostArticle) => a.id !== currentArticle.id);

    // Score each article by counting matching keywords
    const scored = otherArticles.map((article: GhostArticle) => {
        const articleKeywords = extractKeywords(getSearchableText(article));

        // Count how many keywords match (using Set.has() for better performance)
        const matchCount = articleKeywords.filter((keyword: string) =>
            currentKeywordsSet.has(keyword)
        ).length;

        return { article, score: matchCount };
    });

    // Sort by relevance (primary: match count, secondary: newest first)
    scored.sort((a: ScoredArticle, b: ScoredArticle) => {
        // Primary sort: Most keyword matches first
        if (b.score !== a.score) {
            return b.score - a.score;
        }
        // Tiebreaker: Newest article first
        return sortByNewest(b.article, a.article);
    });

    // Filter to only articles with matches, or fallback to newest if no matches
    const articlesWithMatches = scored.filter((s: ScoredArticle) => s.score > 0);

    if (articlesWithMatches.length >= count) {
        // Have enough matches, return top N
        return articlesWithMatches.slice(0, count).map((s: ScoredArticle) => s.article);
    } else if (articlesWithMatches.length > 0) {
        // Have some matches but not enough, return all matches
        return articlesWithMatches.map((s: ScoredArticle) => s.article);
    } else {
        // No keyword matches at all, fallback to newest articles
        return scored
            .sort((a: ScoredArticle, b: ScoredArticle) => sortByNewest(b.article, a.article))
            .slice(0, count)
            .map((s: ScoredArticle) => s.article);
    }
};
